import type { ListPromosResponse, PlanPromo } from "@wallyo/contracts/console/billing";
import { t } from "../../i18n";
import { formatCountry, formatMoney, formatNumber } from "../../i18n/format";
import {
  canEndEarly,
  periodsLabel,
  promoStateName,
  windowLabel,
  PROMO_STATE_TONE,
  type BillingListState,
} from "./billing-model";
import { FailedCard, LoadingCard, RefreshBanner } from "./BillingNotices";

export type PromosViewProps = {
  state: BillingListState<ListPromosResponse>;
  canRunPromos: boolean;
  onNewPromo: () => void;
  onEndPromo: (promo: PlanPromo) => void;
  onRetry: () => void;
};

/**
 * Promos: a lower price for a plan in a country, for anyone who subscribes
 * inside its window. A pro gets the single lowest price, so a promo and a
 * remise never add up (ADR-0018 §4a).
 */
export function PromosView({ state, canRunPromos, onNewPromo, onEndPromo, onRetry }: PromosViewProps) {
  if (!state.data && state.status === "failed") {
    return <FailedCard text={t("billing.promos.loadFailed")} error={state.error} onRetry={onRetry} />;
  }
  if (!state.data) return <LoadingCard text={t("billing.promos.loading")} />;

  const { promos } = state.data;
  return (
    <div className="card">
      <RefreshBanner show={state.status === "failed"} onRetry={onRetry} />
      <p className="foot">{t("billing.promos.intro")}</p>
      <NewPromoButton show={canRunPromos} onNewPromo={onNewPromo} />
      <Empty show={promos.length === 0} />
      <PromoTable promos={promos} canRunPromos={canRunPromos} onEndPromo={onEndPromo} />
    </div>
  );
}

function NewPromoButton({ show, onNewPromo }: { show: boolean; onNewPromo: () => void }) {
  if (!show) return null;
  return (
    <div className="btns">
      <button className="btn primary" type="button" data-action="newPromo" onClick={onNewPromo}>
        {t("billing.action.newPromo")}
      </button>
    </div>
  );
}

function Empty({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="foot" role="status" data-empty="promos">
      {t("billing.promos.empty")}
    </p>
  );
}

function PromoTable({
  promos,
  canRunPromos,
  onEndPromo,
}: {
  promos: readonly PlanPromo[];
  canRunPromos: boolean;
  onEndPromo: (promo: PlanPromo) => void;
}) {
  if (promos.length === 0) return null;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>{t("billing.col.name")}</th>
            <th>{t("billing.col.plan")}</th>
            <th>{t("billing.col.country")}</th>
            <th className="num">{t("billing.col.promoPrice")}</th>
            <th>{t("billing.col.periods")}</th>
            <th>{t("billing.col.window")}</th>
            <th>{t("billing.col.state")}</th>
            <th className="num">{t("billing.col.subscribers")}</th>
            <th>{t("billing.col.createdBy")}</th>
            <th aria-label={t("billing.col.actions")} />
          </tr>
        </thead>
        <tbody>
          {promos.map((promo) => (
            <PromoRow key={promo.planPromoId} promo={promo} canRunPromos={canRunPromos} onEndPromo={onEndPromo} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromoRow({
  promo,
  canRunPromos,
  onEndPromo,
}: {
  promo: PlanPromo;
  canRunPromos: boolean;
  onEndPromo: (promo: PlanPromo) => void;
}) {
  return (
    <tr data-promo={promo.planPromoId} data-state={promo.state}>
      <td>
        <bdi>{promo.name}</bdi>
        <br />
        <span className="meta">
          <bdi>{promo.reason}</bdi>
        </span>
      </td>
      <td>{promo.planName}</td>
      <td>{formatCountry(promo.country)}</td>
      <td className="num">{formatMoney(promo.amount, promo.currency)}</td>
      <td>{periodsLabel(promo.periods)}</td>
      <td className="nowrap">{windowLabel(promo)}</td>
      <td>
        <span className={`pill ${PROMO_STATE_TONE[promo.state]}`}>{promoStateName(promo.state)}</span>
      </td>
      <td className="num" data-subscribers="">
        {formatNumber(promo.subscriberCount)}
      </td>
      <td>
        <bdi>{promo.createdBy}</bdi>
      </td>
      <td className="row-actions">
        <EndButton promo={promo} canRunPromos={canRunPromos} onEndPromo={onEndPromo} />
      </td>
    </tr>
  );
}

function EndButton({
  promo,
  canRunPromos,
  onEndPromo,
}: {
  promo: PlanPromo;
  canRunPromos: boolean;
  onEndPromo: (promo: PlanPromo) => void;
}) {
  if (!canRunPromos || !canEndEarly(promo)) return null;
  return (
    <button className="btn small" type="button" data-end={promo.planPromoId} onClick={() => onEndPromo(promo)}>
      {t("billing.action.endNow")}
    </button>
  );
}
